import { useMemo } from "react"

import type {
    FieldValues,
    UseFormSetValue,
    UseFormWatch,
} from "react-hook-form"

import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import type { IPayer } from "@common-shared/types/responseData/commonResponseData.types.ts"
import { IOneVerifyCoveragePayer } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import type { ISelectRenderedOption } from "@front-shared/types/objects.types.ts"

import { CHECKER_CONFIG } from "../config/checker.config"
import { UtilHelper } from "../helpers/util.helper.tsx"

export const useCoveragePayerSelection = (
    coveragePayers: IOneVerifyCoveragePayer[],
    cptCodes: ISelectRenderedOption[],
    reactHookFormSet: UseFormSetValue<FieldValues>,
    reactHookFormWatch: UseFormWatch<FieldValues>,
    setNetworkFields = true,
    fieldGroupName?: string,
    manualCheckType?: QuotaUsageType,
) => {
    const dataWatcher = reactHookFormWatch()

    const isInNetworkOnlyCheckType = useMemo<boolean>(() => {
        return (
            !!manualCheckType &&
            CHECKER_CONFIG.inNetworkOnlyCheckTypes.includes(manualCheckType)
        )
    }, [manualCheckType])

    const fieldNames = useMemo(() => {
        const inNetwork = fieldGroupName
            ? `${fieldGroupName}.inNetworkCheck`
            : "inNetworkCheck"

        const outNetwork = fieldGroupName
            ? `${fieldGroupName}.outNetworkCheck`
            : "outNetworkCheck"

        const sessionRate = fieldGroupName
            ? `${fieldGroupName}.sessionRate`
            : "sessionRate"

        const payer = fieldGroupName ? `${fieldGroupName}.payer` : "payer"
        const cptCode = fieldGroupName ? `${fieldGroupName}.cptCode` : "cptCode"

        return {
            inNetwork,
            outNetwork,
            sessionRate,
            payer,
            cptCode,
        }
    }, [fieldGroupName])

    const getMatchingCoveragePayer = (
        payer?: IPayer,
        preferredNetwork = PayerCoverageCheckNetwork.IN,
    ): IOneVerifyCoveragePayer | undefined => {
        return (
            coveragePayers.find(
                item =>
                    item.payer.payerId === payer?.payerId &&
                    item.defaultCoverageCheckNetwork === preferredNetwork,
            ) ||
            coveragePayers.find(item => item.payer.payerId === payer?.payerId)
        )
    }

    const availableCptCodeOptions = useMemo<ISelectRenderedOption[]>(() => {
        const preSavedPayerCodes = getMatchingCoveragePayer(
            dataWatcher.payer,
        )?.rates.map(item => item.code)

        // First return preSaved payer values and then all other options
        return [
            ...cptCodes.filter(item =>
                preSavedPayerCodes?.includes(item.value),
            ),

            ...cptCodes.filter(
                item => !preSavedPayerCodes?.includes(item.value),
            ),
        ].map((option, index) => {
            return {
                ...option,

                key: `${option.value}-${index}`,
            }
        })
    }, [dataWatcher.payer, JSON.stringify(cptCodes)])

    const onPayerSelected = (payer: IPayer) => {
        const coveragePayer = getMatchingCoveragePayer(payer)

        if (!coveragePayer) {
            return
        }

        const neededNetwork = isInNetworkOnlyCheckType
            ? PayerCoverageCheckNetwork.IN
            : coveragePayer.defaultCoverageCheckNetwork

        if (setNetworkFields) {
            switch (neededNetwork) {
                case PayerCoverageCheckNetwork.IN:
                    reactHookFormSet(fieldNames.inNetwork, true)
                    reactHookFormSet(fieldNames.outNetwork, false)

                    break

                case PayerCoverageCheckNetwork.OUT:
                    reactHookFormSet(fieldNames.inNetwork, false)
                    reactHookFormSet(fieldNames.outNetwork, true)

                    break

                case PayerCoverageCheckNetwork.IN_OUT:
                    reactHookFormSet(fieldNames.inNetwork, true)
                    reactHookFormSet(fieldNames.outNetwork, true)

                    break
            }
        }

        // Don't update sessionRate if we've updated neededNetwork
        if (neededNetwork !== coveragePayer.defaultCoverageCheckNetwork) {
            return
        }

        setTimeout(() => {
            onCoveragePayerSelected(coveragePayer)
        })
    }

    const onCoveragePayerSelected = (
        coveragePayer: IOneVerifyCoveragePayer,
    ) => {
        reactHookFormSet(fieldNames.payer, coveragePayer.payer)

        if (!coveragePayer.rates.length) {
            return
        }

        onPayerRateSelected(coveragePayer.rates[0].code)
    }

    const onPayerRateSelected = (cptCode: string) => {
        // Set CPT code value
        reactHookFormSet(fieldNames.cptCode, cptCode, {
            shouldValidate: true,
        })

        const currentPayer = reactHookFormWatch(fieldNames.payer) as IPayer
        const currentCoveragePayer = getMatchingCoveragePayer(currentPayer)

        const neededPayerRate = currentCoveragePayer?.rates.find(
            item => item.code === cptCode,
        )

        // If CPT code has pre-saved value for sessionRate -> set is as well
        // Amount comes in cents here -> so convert it to dollars
        if (neededPayerRate) {
            reactHookFormSet(
                fieldNames.sessionRate,
                UtilHelper.formatCents(neededPayerRate.amount).toString(),
                {
                    shouldValidate: true,
                },
            )
        }
    }

    return {
        availableCptCodeOptions,
        onPayerSelected,
        onPayerRateSelected,
    }
}
